// Ground glow under travelling energy (owner 2026-11-02: 'give all blasts and beams a glow that appears underneath as it
// travels'). Render-only: the view feeds it positions it already reads from Sim state / SimEvents each frame.
//
// One pooled InstancedMesh of flat grid patches. Every patch is a capsule-shaped pool (a disc when its length equals its
// width, a stretched stadium along a beam): soft outer falloff plus a slightly brighter cel core band. The vertex stage
// places each grid vertex in world space from per-instance data and lifts it onto the deformable ground by sampling the
// same height texture the ground mesh uses (src/render/ground.ts), so pools hug craters and the Nova Lance trench.
//
// Blending is 'screen' (dst + src·(1 − dst)): it lights dark scorched ground almost fully but only tints bright grass,
// so it never washes out to white the way plain additive did (see boreBlast.ts). Depth-tested, no depth write, so foes,
// props and the hero standing on the glow stay drawn over it. No per-frame allocations; no real lights.
import * as THREE from 'three/webgpu';
import { abs, attribute, float, length, mix, positionGeometry, smoothstep, texture, uv, vec2, vec3 } from 'three/tsl';
import { CROWD, FLASH } from './fx';
import { GROUND_HALF, GroundDeform } from './ground';

/** pools alive at once per quality (a Nova Lance beam is one pool; low quality caps the count) */
const CAP: Record<string, number> = { low: 96, medium: 192, high: 320 };
const FLASH_CAP = 32;
const LIFT = 0.06; // m above the deformed ground (plus a depth bias) so it never z-fights the grass

interface Flash { on: boolean; x: number; z: number; r: number; t: number; life: number; cr: number; cg: number; cb: number }

const _c = new THREE.Color();

export class GroundGlow {
  readonly mesh: THREE.InstancedMesh;
  /** false hides every pool (QA before/after: ?groundglow=0) */
  enabled = true;
  /** follows the Glow (bloom) setting: off draws a dimmer pool whose core stays below the bloom threshold */
  glowOn = true;
  private readonly cap: number;
  private readonly A: Float32Array; private readonly B: Float32Array; private readonly C: Float32Array;
  private readonly attrA: THREE.InstancedBufferAttribute; private readonly attrB: THREE.InstancedBufferAttribute; private readonly attrC: THREE.InstancedBufferAttribute;
  private n = 0;
  private gain = 1; private coreK = 1;
  private readonly flashes: Flash[] = [];
  private nextFlash = 0;

  constructor(scene: THREE.Scene, quality: string, private readonly ground: GroundDeform) {
    this.cap = CAP[quality] ?? CAP.medium;
    this.A = new Float32Array(this.cap * 4); this.B = new Float32Array(this.cap * 4); this.C = new Float32Array(this.cap * 4);
    this.attrA = new THREE.InstancedBufferAttribute(this.A, 4).setUsage(THREE.DynamicDrawUsage) as THREE.InstancedBufferAttribute;
    this.attrB = new THREE.InstancedBufferAttribute(this.B, 4).setUsage(THREE.DynamicDrawUsage) as THREE.InstancedBufferAttribute;
    this.attrC = new THREE.InstancedBufferAttribute(this.C, 4).setUsage(THREE.DynamicDrawUsage) as THREE.InstancedBufferAttribute;
    for (let i = 0; i < FLASH_CAP; i++) this.flashes.push({ on: false, x: 0, z: 0, r: 1, t: 0, life: 1, cr: 0, cg: 0, cb: 0 });

    // grid patch: x = -1..1 along the pool (start → end), y = -1..1 across; dense enough along to follow a beam trench
    const geo = new THREE.PlaneGeometry(2, 2, 16, 6);
    // per-instance vec4s as named geometry attributes (several instancedBufferAttribute() nodes in one material alias)
    geo.setAttribute('ggA', this.attrA); geo.setAttribute('ggB', this.attrB); geo.setAttribute('ggC', this.attrC);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const a = attribute('ggA', 'vec4') as any; // centre x, centre z, unit axis x, unit axis z
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const b = attribute('ggB', 'vec4') as any; // half length, half width, end/start intensity ratio, core strength
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const c = attribute('ggC', 'vec4') as any; // linear rgb premultiplied by intensity
    const g = positionGeometry;
    // across = (az, -ax): keeps the plane's front face pointing up (+y) so the patch isn't back-face culled
    const wx = a.x.add(a.z.mul(g.x).mul(b.x)).add(a.w.mul(g.y).mul(b.y));
    const wz = a.y.add(a.w.mul(g.x).mul(b.x)).sub(a.z.mul(g.y).mul(b.y));
    const h = texture(ground.tex, vec2(wx, wz).add(GROUND_HALF).div(GROUND_HALF * 2)).level(float(0)).x;
    const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, fog: false, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -4 });
    m.positionNode = vec3(wx, h.add(LIFT), wz);
    // capsule distance: 0 on the centre line, 1 at the rim; round end caps of radius = half width
    const along = b.x.mul(abs(uv().x.mul(2).sub(1)));
    const cap = along.sub(b.x.sub(b.y)).max(0).div(b.y);
    const d = length(vec2(uv().y.mul(2).sub(1), cap));
    const soft = smoothstep(1.0, 0.0, d);
    const core = smoothstep(0.46, 0.3, d); // soft-edged brighter cel band in the middle
    const grad = mix(float(1), b.z, uv().x);
    m.colorNode = vec3(c.x, c.y, c.z).mul(soft.mul(soft).add(core.mul(b.w))).mul(grad);
    m.opacityNode = float(1);
    m.blending = THREE.CustomBlending;
    m.blendEquation = THREE.AddEquation;
    m.blendSrc = THREE.OneMinusDstColorFactor; m.blendDst = THREE.OneFactor;
    m.blendSrcAlpha = THREE.ZeroFactor; m.blendDstAlpha = THREE.OneFactor;
    this.mesh = new THREE.InstancedMesh(geo, m, this.cap);
    this.mesh.instanceMatrix.setUsage(THREE.StaticDrawUsage); // identity: placement comes from the instance attributes
    this.mesh.frustumCulled = false; this.mesh.count = 0; this.mesh.renderOrder = 1;
    scene.add(this.mesh);
  }

  /** Start a frame: ages the landing flashes (they take the first slots). */
  begin(dt: number): void {
    this.n = 0;
    // Glow off: dimmer and the core never climbs into bloom range; busy fights dim a little so the field stays readable
    this.gain = (this.glowOn ? 1 : 0.7) * (1 - 0.35 * CROWD.busy);
    this.coreK = this.glowOn ? 1 : 0.4;
    for (const f of this.flashes) {
      if (!f.on) continue;
      f.t += dt;
      if (f.t >= f.life) { f.on = false; continue; }
      const u = f.t / f.life, k = (1 - u) * (1 - u), r = f.r * (0.75 + 0.35 * Math.sqrt(u));
      this.put(f.x, f.z, 1, 0, r, r, f.cr * k, f.cg * k, f.cb * k, 1, 0.9);
    }
  }

  /** A brief ground flash where a blast lands (r = pool radius in m, life in s). Respects reduced flashes. */
  flash(x: number, z: number, r: number, hex: number, k: number, life: number): void {
    k *= FLASH.gate(r >= 4); if (k <= 0.001) return; // a11y: wide ground flashes count against the flash budget
    const f = this.flashes[this.nextFlash]; this.nextFlash = (this.nextFlash + 1) % FLASH_CAP;
    _c.setHex(hex);
    f.on = true; f.x = x; f.z = z; f.r = r; f.t = 0; f.life = life; f.cr = _c.r * k; f.cg = _c.g * k; f.cb = _c.b * k;
  }

  /** A round pool under an orb at height y (world): r = pool radius at ground level, k = strength. The pool spreads and
   *  dims with height so high shots barely touch the floor. */
  blob(x: number, y: number, z: number, r: number, hex: number, k: number, core = 0.8): void {
    if (this.n >= this.cap) return;
    const hgt = Math.max(0, y - this.ground.sample(x, z) - 0.7);
    const att = 1 / (1 + hgt * hgt * 0.07);
    if (att * k < 0.03) return;
    const rr = r * (1 + hgt * 0.08);
    _c.setHex(hex); const s = k * att * this.gain;
    this.put(x, z, 1, 0, rr, rr, _c.r * s, _c.g * s, _c.b * s, 1, core);
  }

  /** A stretched pool along a beam from (x0,z0) to (x1,z1) at height y: hw = half width (m), kEnd = strength at the far
   *  end relative to the start (linear between). */
  strip(x0: number, z0: number, x1: number, z1: number, y: number, hw: number, hex: number, k: number, kEnd = 1, core = 0.8): void {
    if (this.n >= this.cap) return;
    const dx = x1 - x0, dz = z1 - z0, L = Math.hypot(dx, dz);
    if (L < 1e-3) { this.blob(x0, y, z0, hw, hex, k, core); return; }
    const hgt = Math.max(0, y - this.ground.sample((x0 + x1) / 2, (z0 + z1) / 2) - 0.7);
    const att = 1 / (1 + hgt * hgt * 0.07);
    _c.setHex(hex); const s = k * att * this.gain;
    // the capsule's end caps reach past the end points by hw, so the pool starts at the hands and ends at the tip
    const half = L / 2 + hw;
    this.put((x0 + x1) / 2, (z0 + z1) / 2, dx / L, dz / L, half, hw, _c.r * s, _c.g * s, _c.b * s, kEnd, core);
  }

  private put(x: number, z: number, ax: number, az: number, half: number, hw: number, r: number, g: number, b: number, kEnd: number, core: number): void {
    if (this.n >= this.cap) return;
    const o = this.n * 4;
    this.A[o] = x; this.A[o + 1] = z; this.A[o + 2] = ax; this.A[o + 3] = az;
    this.B[o] = half; this.B[o + 1] = hw; this.B[o + 2] = kEnd; this.B[o + 3] = core * this.coreK;
    this.C[o] = r; this.C[o + 1] = g; this.C[o + 2] = b; this.C[o + 3] = 0;
    this.n++;
  }

  /** Upload this frame's pools. */
  end(): void {
    const n = this.enabled ? this.n : 0;
    this.mesh.count = n;
    if (n) { this.attrA.needsUpdate = true; this.attrB.needsUpdate = true; this.attrC.needsUpdate = true; }
  }

  clear(): void { for (const f of this.flashes) f.on = false; this.n = 0; this.mesh.count = 0; }
}
